import { Box, Button, Columns, Container, Form, Heading, Icon, Section } from 'react-bulma-components';
import { INSTRUMENTS_BY_KEY, INSTRUMENT_ICON_BY_NAME, INSTRUMENT_BY_NAME } from '../lib/instruments';
import React, { Fragment, useState } from 'react';
import { faCheckCircle, faEnvelope, faMusic, faUser } from '@fortawesome/free-solid-svg-icons';
import { gql, useMutation, useQuery } from '@apollo/client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../components/loader';
import PageTitle from '../components/pagetitle';
import _ from 'lodash';
import { colors } from '../styles';

const { Field, Control, Input, Label, Select } = Form;

export const GET_PROFILE = gql`
   query currentUser{
    myProfile {
      id
      name
      email
      instrument {
        name
        key
        clef
      }
    }
  }
`;

const UPDATE_MY_PROFILE = gql`
  mutation UpdateMyProfile($input: UserProfileInput!) {
    updateMyProfile(input: $input) {
      id
      name
      instrument {
        name
        key
        clef
      }
    }
  }
`;

const Profile = () => {
  const { data, loading, error } = useQuery(GET_PROFILE,
    {
    fetchPolicy: "no-cache"
  });
  const [updateMyProfile, { loading: saving }] = useMutation(UPDATE_MY_PROFILE);
  const [name, setName] = useState(null);
  const [instrumentName, setInstrumentName] = useState(null);
  // Baseline to compare/save against. Starts as the initial fetch, but gets
  // replaced with the mutation's response after a successful save — `data`
  // itself never updates (fetchPolicy: "no-cache"), so comparing against it
  // directly would keep judging changes against stale, pre-save values.
  const [savedProfile, setSavedProfile] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  if (loading) return (
    <Loader />
  );
  if(error && _.get(error,"graphQLErrors[0].extensions.code")==="UNAUTHENTICATED"){
    return (
      <p>Please login</p>
    )
  }
  if (error) return  (
    <Fragment>{error.message}</Fragment>);

  const baseline = savedProfile ?? data.myProfile;
  const currentName = name ?? baseline.name ?? '';
  const currentInstrumentName = instrumentName ?? baseline.instrument?.name ?? '';
  const hasChanges = currentName !== (baseline.name ?? '')
    || currentInstrumentName !== (baseline.instrument?.name ?? '');

  const handleSave = async () => {
    const { data: result } = await updateMyProfile({ variables: { input: {
      name: currentName,
      instrument: INSTRUMENT_BY_NAME[currentInstrumentName],
    } } });
    setSavedProfile(result.updateMyProfile);
    setName(null);
    setInstrumentName(null);
    setJustSaved(true);
  };

  return (
    <Fragment>
      <PageTitle
        name={baseline.name || data.myProfile.email || ''}
        description='Manage your account and practice preferences'></PageTitle>
      <Section>
        <Container>
          <Columns centered>
            <Columns.Column size={6}>
              <Box>
                <Heading size={5}>Account</Heading>

                <Field>
                  <Label>Email</Label>
                  <p style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon><FontAwesomeIcon icon={faEnvelope} color={colors.textSecondary} /></Icon>
                    &nbsp;{data.myProfile.email || '—'}
                  </p>
                </Field>

                <Field>
                  <Label>Display name</Label>
                  <Control iconLeft>
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={currentName}
                      onChange={evt => {
                        setName(evt.target.value);
                        setJustSaved(false);
                      }}
                    />
                    <Icon align="left"><FontAwesomeIcon icon={faUser} /></Icon>
                  </Control>
                </Field>

                <Heading size={5} renderAs="p" style={{ marginTop: '2rem' }}>Practice preferences</Heading>
                <Field>
                  <Label>Instrument</Label>
                  <Control>
                    <Select
                      value={currentInstrumentName}
                      onChange={evt => {
                        setInstrumentName(evt.target.value);
                        setJustSaved(false);
                      }}
                    >
                      <option value="" disabled>Choose an instrument</option>
                      {Object.entries(INSTRUMENTS_BY_KEY).map(([key, instruments]) => (
                        <optgroup key={key} label={`${key} instruments`}>
                          {instruments.map(({ name }) => (
                            <option key={name} value={name}>{INSTRUMENT_ICON_BY_NAME[name]} {name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </Select>
                  </Control>
                </Field>

                <Field kind="group" style={{ marginTop: '1.5rem', alignItems: 'center' }}>
                  <Control>
                    <Button
                      color="primary"
                      loading={saving}
                      disabled={!hasChanges}
                      onClick={handleSave}
                    >
                      Save changes
                    </Button>
                  </Control>
                  {justSaved && (
                    <Control>
                      <span style={{ color: colors.green }}>
                        <Icon><FontAwesomeIcon icon={faCheckCircle} /></Icon>&nbsp;Saved
                      </span>
                    </Control>
                  )}
                </Field>
              </Box>
            </Columns.Column>
          </Columns>
        </Container>
      </Section>
    </Fragment>
  )
}

export default Profile;
